/**
 * Opens on the fund's staged bank transactions, since most payments are
 * already there. Manual entry stays behind a link.
 */

import { getTodayAsISODate } from '@/utils/date-operations';
import { useState } from 'react';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Label } from '@/components/ui/label';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { AlertCircle, Loader2, DollarSign } from 'lucide-react';
import { useRecordFullPayment } from '@/hooks/react-query/useDrawdowns';
import {
  useBankIncomingList,
  useSettleNoticeFromBankLine,
} from '@/hooks/react-query/useBankIncoming';
import { BankLineCandidateList } from './BankLineCandidateList';
import { formatCurrency } from '@/utils/formatters';
import type {
  BatchInvestorNotice,
  RecordFullPaymentRequest,
} from '@/types/drawdown';

const PAYMENT_METHODS = [
  { value: 'wire_transfer', label: 'Wire Transfer' },
  { value: 'sepa', label: 'SEPA' },
  { value: 'swift', label: 'SWIFT' },
  { value: 'ach', label: 'ACH' },
  { value: 'check', label: 'Check' },
] as const;

interface RecordPaymentModalProps {
  open: boolean;
  onClose: () => void;
  onSuccess?: () => void;
  fundId: string;
  drawdownId: string;
  /** The call's currency. Only lines in it can settle a notice. */
  currency: string;
  investor: BatchInvestorNotice;
}

interface FormData {
  payment_date: string;
  payment_method: string;
  reference_number: string;
  notes: string;
  bank_statement_reference: string;
}

// Enough that the wanted line is nearly always here without pulling a whole
// backlog into a dialog. The rest are named below the list.
const CANDIDATE_PAGE_SIZE = 100;

export function RecordPaymentModal({
  open,
  onClose,
  onSuccess,
  fundId,
  drawdownId,
  currency,
  investor,
}: RecordPaymentModalProps) {
  const [error, setError] = useState<string | null>(null);
  const [formData, setFormData] = useState<FormData>(() => ({
    payment_date: getTodayAsISODate(),
    payment_method: 'wire_transfer',
    reference_number: investor.reference_number ?? '',
    notes: '',
    bank_statement_reference: '',
  }));

  const [manualEntry, setManualEntry] = useState(false);
  const [selectedLineId, setSelectedLineId] = useState<string | null>(null);

  const recordPaymentMutation = useRecordFullPayment();
  const settleFromLine = useSettleNoticeFromBankLine({ fundId });
  const candidates = useBankIncomingList(
    { match_status: 'UNMATCHED', page_size: CANDIDATE_PAGE_SIZE },
    { fundId }
  );
  const lines = candidates.data?.items ?? [];
  const linesNotShown = (candidates.data?.total_count ?? 0) - lines.length;
  const selectedLine = lines.find((line) => line.id === selectedLineId) ?? null;

  const handleInputChange = (field: keyof FormData, value: string) => {
    setFormData((prev) => ({ ...prev, [field]: value }));
  };

  const handleMatch = async () => {
    if (selectedLine === null) return;
    try {
      setError(null);
      await settleFromLine.mutateAsync({
        incomingId: selectedLine.id,
        amount: selectedLine.amount,
        drawdownInvestorId: investor.id,
      });
      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to settle from bank line:', err);
      setError(
        err instanceof Error
          ? err.message
          : 'Failed to record payment from that bank transaction'
      );
    }
  };

  const handleSubmit = async () => {
    try {
      setError(null);

      // Validate required fields
      if (!formData.payment_date) {
        setError('Payment date is required');
        return;
      }
      if (!formData.payment_method) {
        setError('Payment method is required');
        return;
      }
      if (investor.net_amount_due <= 0) {
        setError('There is no outstanding amount due for this investor');
        return;
      }

      const requestData: RecordFullPaymentRequest = {
        payment_date: formData.payment_date,
        payment_method:
          formData.payment_method as RecordFullPaymentRequest['payment_method'],
        reference_number: formData.reference_number || undefined,
        notes: formData.notes || undefined,
        bank_statement_reference:
          formData.bank_statement_reference || undefined,
      };

      await recordPaymentMutation.mutateAsync({
        fundId,
        drawdownId,
        drawdownInvestorId: investor.id,
        data: requestData,
      });

      onSuccess?.();
      onClose();
    } catch (err: unknown) {
      console.error('Failed to record payment:', err);
      const errorMessage =
        err instanceof Error ? err.message : 'Failed to record payment';
      setError(errorMessage);
    }
  };

  const remainingAmount = investor.net_amount_due;
  const busy = recordPaymentMutation.isPending || settleFromLine.isPending;

  // Reset form state when modal closes by not rendering content when closed
  if (!open) {
    return null;
  }

  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="flex max-h-[90vh] max-w-2xl flex-col overflow-hidden">
        <DialogHeader className="flex-none pr-8">
          <DialogTitle className="flex items-center gap-2">
            <DollarSign className="h-5 w-5 text-green-600" />
            Record Payment
          </DialogTitle>
          <DialogDescription>
            Record payment received from {investor.investor_name}
          </DialogDescription>
        </DialogHeader>

        {error && (
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        )}

        <DialogBody>
          {/* Payment Amount (Read-only) */}
          <div className="rounded-lg bg-muted p-4">
            <div className="text-sm text-muted-foreground">Payment Amount</div>
            <div className="text-2xl font-semibold">
              {formatCurrency(remainingAmount, { currency })}
            </div>
            <div className="text-xs text-muted-foreground mt-1">
              Full remaining balance
            </div>
          </div>

          {!manualEntry && (
            <div className="space-y-3">
              <Label>Bank transaction</Label>
              {candidates.isLoading ? (
                <p className="text-sm text-muted-foreground">
                  Loading bank transactions…
                </p>
              ) : (
                <>
                  <BankLineCandidateList
                    lines={lines}
                    remainingAmount={remainingAmount}
                    currency={currency}
                    investorIban={investor.investor_iban}
                    selectedId={selectedLineId}
                    onSelect={setSelectedLineId}
                  />
                  {linesNotShown > 0 && (
                    <p className="text-xs text-muted-foreground">
                      Showing the {lines.length} most recent unmatched
                      transactions; {linesNotShown} more are waiting in Incoming
                      Transactions.
                    </p>
                  )}
                </>
              )}
              <Button
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => setManualEntry(true)}
              >
                Record manually instead
              </Button>
            </div>
          )}

          {manualEntry && (
            <>
              {/* Payment Date */}
              <div className="grid gap-2">
                <Label htmlFor="payment_date">Payment Date *</Label>
                <Input
                  id="payment_date"
                  type="date"
                  value={formData.payment_date}
                  onChange={(e) =>
                    handleInputChange('payment_date', e.target.value)
                  }
                />
              </div>

              {/* Payment Method */}
              <div className="grid gap-2">
                <Label htmlFor="payment_method">Payment Method *</Label>
                <Select
                  value={formData.payment_method}
                  onValueChange={(value) =>
                    handleInputChange('payment_method', value)
                  }
                >
                  <SelectTrigger>
                    <SelectValue placeholder="Select payment method" />
                  </SelectTrigger>
                  <SelectContent>
                    {PAYMENT_METHODS.map((method) => (
                      <SelectItem key={method.value} value={method.value}>
                        {method.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>

              {/* Reference Number */}
              <div className="grid gap-2">
                <Label htmlFor="reference_number">Reference Number</Label>
                <Input
                  id="reference_number"
                  value={formData.reference_number}
                  onChange={(e) =>
                    handleInputChange('reference_number', e.target.value)
                  }
                  placeholder="Bank reference or transaction ID"
                />
              </div>

              {/* Bank Statement Reference */}
              <div className="grid gap-2">
                <Label htmlFor="bank_statement_reference">
                  Bank Statement Reference
                </Label>
                <Input
                  id="bank_statement_reference"
                  value={formData.bank_statement_reference}
                  onChange={(e) =>
                    handleInputChange(
                      'bank_statement_reference',
                      e.target.value
                    )
                  }
                  placeholder="Bank statement line reference for reconciliation"
                />
                <p className="text-xs text-muted-foreground">
                  Adding this marks the transaction as bank reconciled
                </p>
              </div>

              {/* Notes */}
              <div className="grid gap-2">
                <Label htmlFor="notes">Notes</Label>
                <Textarea
                  id="notes"
                  value={formData.notes}
                  onChange={(e) => handleInputChange('notes', e.target.value)}
                  placeholder="Optional notes about this payment"
                  rows={2}
                />
              </div>
              <Button
                variant="link"
                className="h-auto p-0 text-sm"
                onClick={() => setManualEntry(false)}
              >
                Choose a bank transaction instead
              </Button>
            </>
          )}
        </DialogBody>

        <DialogFooter className="flex-none">
          <Button variant="outline" onClick={onClose}>
            Cancel
          </Button>
          <Button
            onClick={manualEntry ? handleSubmit : handleMatch}
            disabled={
              busy ||
              remainingAmount <= 0 ||
              (!manualEntry && selectedLine === null)
            }
          >
            {busy ? (
              <>
                <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                Recording...
              </>
            ) : (
              'Record Payment'
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
